import {BrandingReadiness, preloadBrandImages} from '@screenleads/branding';
import { Injectable, signal, inject, untracked } from '@angular/core';
import { SlCompanyColorsService } from 'sl-dev-components';
import { firstValueFrom, timeout } from 'rxjs';
import { CompanyService } from '../../features/companies/services/company.service';

type CompanyBranding = {
  companyId: number | null;
  companyName: string;
  logoUrl: string | null;
  logoWideUrl: string | null;
  logoSquareUrl: string | null;
  logoOnDarkUrl: string | null;
  principalColor: string;
  secondaryColor: string;
  accentColor: string;
  base1Color: string;
  base2Color: string;
  textPrimaryColor: string;
  textSecondaryColor: string;
  backgroundColor: string;
  surfaceColor: string;
  borderColor: string;
  successColor: string;
  warningColor: string;
  errorColor: string;
  infoColor: string;
  componentBranding?: Record<string, string | null | undefined> & {
    sidebarBackgroundColor?: string | null;
    sidebarTextColor?: string | null;
    sidebarTextSecondaryColor?: string | null;
    sidebarHoverBackgroundColor?: string | null;
    sidebarActiveTextColor?: string | null;
    sidebarActiveIndicatorColor?: string | null;
  } | null;
};

@Injectable({ providedIn: 'root' })
export class CompanyThemeService {
  private readonly sharedColors = inject(SlCompanyColorsService);
  private readonly defaultBranding: CompanyBranding = {
    ...this.sharedColors.defaultsColors,
    companyId: null, companyName: 'ScreenLeads',
    logoUrl: null, logoWideUrl: null, logoSquareUrl: null, logoOnDarkUrl: null,
    base1Color: this.sharedColors.defaultsColors.backgroundColor,
    base2Color: this.sharedColors.defaultsColors.surfaceColor,
    componentBranding: null
  };

  readonly branding = signal<CompanyBranding>(this.defaultBranding);
  readonly readiness = new BrandingReadiness();
  readonly ready = this.readiness.ready;
  readonly failed = this.readiness.failed;
  private themeRequest = 0;
  private requestedCompanyId = 1;
  private pendingTheme: {id:number; promise:Promise<void>} | null = null;
  retry(): void { this.loadCompanyTheme(this.requestedCompanyId); }


  constructor(private companyService: CompanyService) {
    this.applyBranding(this.defaultBranding);
  }

  loadCompanyTheme(companyId: number | null | undefined): void {
    void this.loadCompanyThemeAsync(companyId || 1);
  }

  applyFetched(company:any,companyId:number): void {
    const request=++this.themeRequest;
    this.requestedCompanyId=companyId;
    this.readiness.markPending();
    void this.commitCompany(company,companyId,request).catch(()=>{if(request===this.themeRequest)this.readiness.markFailed();});
  }

  loadCompanyThemeAsync(companyId:number):Promise<void> {
    if(this.pendingTheme?.id===companyId)return this.pendingTheme.promise;
    this.requestedCompanyId=companyId;
    const request=++this.themeRequest;
    if(!untracked(this.ready)||untracked(this.branding).companyId!==companyId)this.readiness.markPending();
    const promise=(async()=>{
      try {
        const company=await firstValueFrom(this.companyService.getPublicBranding(companyId).pipe(timeout(15000)));
        if(request===this.themeRequest)await this.commitCompany(company,companyId,request);
      }catch{if(request===this.themeRequest)this.readiness.markFailed();}
    })();
    this.pendingTheme={id:companyId,promise};
    void promise.finally(()=>{if(this.pendingTheme?.promise===promise)this.pendingTheme=null;});
    return promise;
  }

  private async commitCompany(company:any,companyId:number,request:number):Promise<void>{
    if(!company||Array.isArray(company)||typeof company.name!=='string'||(company.id!=null&&Number(company.id)!==companyId))throw Error('Invalid company branding');
    const urls=[company.logo?.src,company.logoWide?.src,company.logoSquare?.src,company.logoOnDark?.src,company.logoUrl,company.logoWideUrl,company.logoHorizontalUrl,company.logoSquareUrl,company.logoCompactUrl,company.logoOnDarkUrl,company.logoLightUrl].map(url=>this.resolveMediaUrl(url));
    await preloadBrandImages(urls);
    if(request!==this.themeRequest)return;
    this._applyCompanyData(company,companyId);
    this.readiness.markReady();
  }

  private _applyCompanyData(company: any, companyId: number): void {
    const logoMain = this.resolveMediaUrl(
      company.logo?.src || company.logoUrl || company.logoHorizontalUrl || null
    );
    const logoWide = this.resolveMediaUrl(
      company.logoWide?.src || company.logoWideUrl || company.logoHorizontalUrl || logoMain
    );
    const logoSquare = this.resolveMediaUrl(
      company.logoSquare?.src || company.logoSquareUrl || company.logoCompactUrl || logoMain
    );
    const logoOnDark = this.resolveMediaUrl(
      company.logoOnDark?.src || company.logoOnDarkUrl || company.logoLightUrl || logoWide || logoMain
    );

    const branding: CompanyBranding = {
      companyId: company.id ?? companyId,
      companyName: company.name || 'ScreenLeads',
      logoUrl: logoMain,
      logoWideUrl: logoWide,
      logoSquareUrl: logoSquare,
      logoOnDarkUrl: logoOnDark,
      principalColor: company.principalColor || this.defaultBranding.principalColor,
      secondaryColor: company.secondaryColor || this.defaultBranding.secondaryColor,
      accentColor: company.accentColor || this.defaultBranding.accentColor,
      base1Color: company.base1Color || this.defaultBranding.base1Color,
      base2Color: company.base2Color || this.defaultBranding.base2Color,
      textPrimaryColor: company.textPrimaryColor || this.defaultBranding.textPrimaryColor,
      textSecondaryColor: company.textSecondaryColor || this.defaultBranding.textSecondaryColor,
      backgroundColor: company.backgroundColor || this.defaultBranding.backgroundColor,
      surfaceColor: company.surfaceColor || this.defaultBranding.surfaceColor,
      borderColor: company.borderColor || this.defaultBranding.borderColor,
      successColor: company.successColor || this.defaultBranding.successColor,
      warningColor: company.warningColor || this.defaultBranding.warningColor,
      errorColor: company.errorColor || this.defaultBranding.errorColor,
      infoColor: company.infoColor || this.defaultBranding.infoColor,
      componentBranding: company.componentBranding ?? null
    };

    this.branding.set(branding);
    this.applyBranding(branding);
  }

  clearTheme(): void {
    ++this.themeRequest;
    this.pendingTheme=null;
    this.readiness.markPending();
    this.branding.set(this.defaultBranding);
    this.applyBranding(this.defaultBranding);
    this.loadCompanyTheme(1);
  }

  private applyBranding(branding: CompanyBranding): void {
    this.sharedColors.setOperationalCompany({...branding, id: branding.companyId});
  }

  private resolveMediaUrl(url: string | null | undefined): string | null {
    if (!url) {
      return null;
    }

    const trimmed = url.trim();
    if (!trimmed) {
      return null;
    }

    if (/^(https?:|data:|blob:)/i.test(trimmed)) {
      return trimmed;
    }

    return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  }

}
